import { Card, CardContent, CardHeader, Fab, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import {
  getNotasProfesorList,
  createNotaProfesor,
  deleteNotaProfesor
} from '../../../../utils/api/alumnos'
import { dateToStringLong, dateToTimeString } from '../../../../utils/date'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import Dropdown from '../../../../components/Dropdown/Dropdown'
import { useState } from 'react'
import css from './NotasProfesor.module.css'
import ModalNuevaNotaProfesor from '../../../../modals/ModalNuevaNotaProfesor/ModalNuevaNotaProfesor'

const NotasProfesor = ({ alumnoId, alumno }) => {
  const queryClient = useQueryClient()
  const notification = useNotification()

  const [open, setOpen] = useState(false)

  const { isLoading, data: notasProfesorList = [] } = useQuery({
    queryKey: ['alumnos', 'notas-profesor', 'list', alumnoId],
    queryFn: () =>
      getNotasProfesorList(alumnoId)
        .then(datos => datos.reverse())
        .catch(err => {
          notification.error({
            title: 'Error obteniendo notas de alumno',
            content: err
          })
          return []
        })
  })

  const { isPending: isCreandoNota, mutate: crearNota } = useMutation({
    mutationFn: createNotaProfesor,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['alumnos', 'notas-profesor', 'list', alumnoId]
      })
      notification.success({
        title: 'Nota creada',
        content: 'Se ha creado correctamente'
      })
      setOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error al guardar la nota', content: err })
    }
  })

  const { isPending: isBorrandoNota, mutate: borrarNota } = useMutation({
    mutationFn: deleteNotaProfesor,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['alumnos', 'notas-profesor', 'list', alumnoId]
      })
      notification.success({
        title: 'Nota borrada',
        content: 'Se ha borrado la nota'
      })
    },
    onError: err => {
      notification.error({ title: 'Error al borrar la nota', content: err })
    }
  })

  const handleSubmit = ({ nota }) => {
    if (isCreandoNota) return
    crearNota({ nota, alumno: alumnoId })
  }

  const handleDeleteNote = id => {
    if (isBorrandoNota) return
    borrarNota(id)
  }

  const handleOptionClick = ({ id, option }) => {
    if (option === 'Eliminar nota') handleDeleteNote(id)
  }

  return (
    <PageLoading isLoading={isLoading}>
      {notasProfesorList.length === 0 && (
        <Typography className={css.placeholder} variant='h6'>
          Este alumno no tiene ninguna anotación de profesores en su ficha
        </Typography>
      )}
      {notasProfesorList.map(nota => (
        <div className={css.card} key={nota.id}>
          <Card>
            <CardHeader
              className={css.header}
              title={`${dateToTimeString(nota.fecha)} - ${dateToStringLong(nota.fecha)}`}
              titleTypographyProps={{ variant: 'body1' }}
              subheader={`${nota.usuario} escribió:`}
              action={
                <Dropdown
                  id={nota.id}
                  options={['Eliminar nota']}
                  onOptionClick={option =>
                    handleOptionClick({ id: nota.id, option })
                  }
                />
              }
            />
            <CardContent>
              <Typography variant='body1'>{nota.nota}</Typography>
            </CardContent>
          </Card>
        </div>
      ))}
      <Fab
        className={css.fab}
        color='primary'
        aria-label='add'
        onClick={() => setOpen(true)}
      >
        <i className='material-icons'>add</i>
      </Fab>
      <ModalNuevaNotaProfesor
        open={open}
        alumno={alumno}
        onSubmit={handleSubmit}
        onClose={() => setOpen(false)}
      />
    </PageLoading>
  )
}

export default NotasProfesor
